export const handleFileDownload = async (url: string, fileName: string, token?: string | null, setIsDownloading?: (isDownloading: boolean) => void) => {
  const makeRequest = async (authToken: string | null) => {
    const response = await fetch(url, {
      method: 'GET',
      ...(authToken && { headers: { Authorization: `Bearer ${authToken}` } })
    });

    if (!response.ok) {
      const errorData = await response.text();
      let errorMessage;

      try {
        const parsedError = JSON.parse(errorData);
        errorMessage = parsedError.message;
      } catch {
        errorMessage = errorData;
      }

      const error = new Error(errorMessage) as any;
      error.status = response.status;
      error.message = errorMessage;
      error.response = { status: response.status, data: { message: errorMessage } };
      throw error;
    }

    return response;
  };

  try {
    if (setIsDownloading) {
      setIsDownloading(true);
    }
    let response = await makeRequest(token || null);

    // Get the content type and file extension from response headers
    const contentType = response.headers.get('content-type') || 'application/octet-stream';
    const fileExtension = response.headers.get('x-file-extension');
    const originalFileName = response.headers.get('x-file-name');

    // Create blob with correct MIME type
    const data = await response.blob();
    const blob = new Blob([data], { type: contentType });
    const blobUrl = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = blobUrl;

    // Use the file extension from backend if available, otherwise use the original filename
    if (fileExtension) {
      if (!fileName) {
        a.download = `${originalFileName}`;
      } else {
        // Remove existing extension and add the correct one from backend
        const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
        a.download = `${nameWithoutExt}.${fileExtension}`;
      }
    } else {
      a.download = fileName;
    }
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error: any) {
    console.error('Download failed:', error);

    // Handle specific error cases
    if (error?.status === 403) {
      if ((error?.message === 'Invalid token' || error?.message === 'Unauthorized') && token) {
        // Try to refresh the token once
        try {
          const apiUrl = import.meta.env.VITE_API_URL || '/api';
          const refreshResponse = await fetch(`${apiUrl}/auth/refresh`, {
            method: 'POST',
            credentials: 'include'
          });

          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            const newToken = refreshData.accessToken;

            // Update localStorage with new token
            localStorage.setItem('accessToken', newToken);

            // Retry the download with the new token
            const retryResponse = await makeRequest(newToken);

            // Get the content type and file extension from response headers
            const contentType = retryResponse.headers.get('content-type') || 'application/octet-stream';
            const fileExtension = retryResponse.headers.get('x-file-extension');

            // Create blob with correct MIME type
            const data = await retryResponse.blob();
            const blob = new Blob([data], { type: contentType });
            const blobUrl = window.URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = blobUrl;

            // Use the file extension from backend if available, otherwise use the original filename
            if (fileExtension) {
              // Remove existing extension and add the correct one from backend
              const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
              a.download = `${nameWithoutExt}.${fileExtension}`;
            } else {
              a.download = fileName;
            }
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(blobUrl);
            return; // Success, exit the function
          } else {
            throw new Error('Token refresh failed');
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          throw new Error('Your session has expired. Please refresh the page and try again.');
        }
      } else {
        throw new Error('You are not authorized to download this file.');
      }
    } else if (error?.status === 404) {
      throw new Error('File not found. Please check the download link and try again.');
    } else if (error?.status >= 500) {
      throw new Error('Server error. Please try again later.');
    } else {
      throw error; // Re-throw original error for other cases
    }
  } finally {
    if (setIsDownloading) {
      setIsDownloading(false);
    }
  }
};